import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../utils";
import {
  saveNodeType,
  isHoverForInteractiveType,
  getValueFromOverride,
  terminate,
} from "./utils";
import EachOrderInteractive from "./wrapper/EachOrderInteractive";
import { computeTicksBetween } from "../../../utils/priceUtils";

/**
 * OrderInteractive
 * --------------------------------------------------
 * This component manages a list of interactive main orders and their optional OTO (One-Triggers-Other) legs
 * (typically stop & profit). The critical invariant we preserve is: once the user manually adjusts an OTO leg,
 * the relative distance they established (expressed as deltaTicks from the main order) must remain stable through
 * subsequent main order drags.
 *
 * Core Concepts
 * 1. deltaTicks: Relative offset (in minimum price ticks) between the main order price and an OTO leg. When the
 *    main order is dragged, we recompute each OTO absolute price = offsetByTicks(symbol, newMainPrice, deltaTicks).
 * 2. User Adjustment Flow:
 *    - User drags an OTO leg → we update its absolute price AND (elsewhere) its stored deltaTicks to reflect the new
 *      relative spacing vs the current main order. This freezes the spacing they just defined.
 *    - User then drags ONLY an OTO leg: We must not let a stray main order dragComplete recalculation overwrite its
 *      freshly adjusted absolute price using an obsolete main price.
 * 3. Guarding Against Phantom Main Drags:
 *    - We introduce an instance flag `mainOrderDragging` that is set only on a genuine main order drag start.
 *    - Global flag `window.oTODragInProgress` (set externally) indicates an OTO drag lifecycle is active.
 *    - handleDragComplete early-outs unless `mainOrderDragging` is true AND no OTO drag is in progress.
 *
 * Why no direct mutation of draggedOrder.price?
 * - We previously mutated `draggedOrder.price` during override which could cause stale snapshots and unintended
 *   overwrites of OTO price arrays. Now we keep the transient drag price solely in component state (override) until
 *   completion, then emit a single `onUpdateOrderPrice` action.
 *
 * Result
 * - Adjusting OTO legs establishes new deltaTicks. Dragging the main order afterwards re-applies those deltas
 *   deterministically. OTO-only drags will not trigger unwanted main recalculations, preserving user intent.
 */

class OrderInteractive extends Component {
  constructor(props) {
    super(props);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawOrder = this.handleDrawOrder.bind(this);
    this.terminate = terminate.bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState = isHoverForInteractiveType("orderList").bind(this);

    this.nodes = [];
    this.state = { override: null };
    this.mainOrderDragging = false; // guard flag for dragComplete
    this.currentOverride = null; // synchronous tracking for fast drags (setState is async)
  }

  handleDragStart(index) {
    if (window.oTODragInProgress) return; // ignore if OTO drag active
    this.mainOrderDragging = true;
    this.dragStartIndex = index;
    const { orderList } = this.props;
    const main = orderList[index];
    // Use effective price: price field, or fall back to limit/stop from router orders
    this.preDragMainPrice = main
      ? (main.price ?? main.limit ?? main.stop)
      : undefined;

    // Store references for potential deltaTicks derivation later
    this.pendingDeltaDerivation = null;
    if (main && Array.isArray(main.oto)) {
      const effectiveMainPrice = main.price ?? main.limit ?? main.stop;
      this.pendingDeltaDerivation = {
        main,
        effectiveMainPrice,
        hasUnresolvedDeltas: false,
      };
      main.oto.forEach((oto) => {
        const effectiveOTOPrice = oto.price ?? oto.limit ?? oto.stop;
        if (
          oto.deltaTicks === undefined &&
          isDefined(effectiveMainPrice) &&
          isDefined(effectiveOTOPrice)
        ) {
          this.pendingDeltaDerivation.hasUnresolvedDeltas = true;
        }
      });
    }

    // Emit an early drag state so integration layer can refresh relative distance cache BEFORE first movement
    if (this.props.onDragStateChange && main && isDefined(main.price)) {
      this.props.onDragStateChange({ index, price: main.price, started: true });
    }
  }

  handleDrag(index, newPrice) {
    if (window.oTODragInProgress) return;
    if (!this.mainOrderDragging) this.mainOrderDragging = true; // defensive

    // On first actual drag movement, derive missing deltaTicks if needed
    if (
      this.pendingDeltaDerivation &&
      this.pendingDeltaDerivation.hasUnresolvedDeltas &&
      this.props.onOTOEdit
    ) {
      const { main, effectiveMainPrice } = this.pendingDeltaDerivation;
      let derivedAny = false;
      main.oto.forEach((oto) => {
        const effectiveOTOPrice = oto.price ?? oto.limit ?? oto.stop;
        if (
          oto.deltaTicks === undefined &&
          isDefined(effectiveMainPrice) &&
          isDefined(effectiveOTOPrice)
        ) {
          const derived = computeTicksBetween(
            oto.symbol,
            effectiveMainPrice,
            effectiveOTOPrice,
          );
          if (derived !== undefined) {
            // Persist derived delta so future drags remain stable
            // Mark as programmatic so distance cache isn't treated as a manual user edit
            this.props.onOTOEdit(main.id, oto.id, effectiveOTOPrice, {
              deltaTicks: derived,
              programmatic: true,
            });
            derivedAny = true;
          }
        }
      });
      this.missingDeltasResolvedThisDrag = derivedAny;
      // Clear pending derivation so it only happens once per drag
      this.pendingDeltaDerivation = null;
    }

    const newOverride = { index, price: newPrice };
    this.currentOverride = newOverride; // synchronous update for fast drags
    this.setState({ override: newOverride });
    const { onDragStateChange } = this.props;
    if (onDragStateChange) onDragStateChange(newOverride);
  }

  handleDragComplete() {
    // Guard: Only process if a legitimate main order drag started AND no OTO drag is active.
    // This prevents a stale dragComplete (e.g., triggered after an OTO drag) from recomputing OTO prices.
    if (window.oTODragInProgress || !this.mainOrderDragging) {
      if (window.oTODragInProgress) {
        console.warn(
          "⚠️ OrderInteractive.handleDragComplete: Skipped because oTODragInProgress is true",
        );
      }
      if (!this.mainOrderDragging) {
        console.warn(
          "⚠️ OrderInteractive.handleDragComplete: Skipped because mainOrderDragging is false",
        );
      }
      return; // silent skip – intentional suppression
    }

    // Use synchronous instance variable instead of async state for fast drags
    const override = this.currentOverride;
    if (!isDefined(override)) {
      console.warn(
        "⚠️ OrderInteractive.handleDragComplete: No override state (drag started but no movement detected)",
      );
      this.mainOrderDragging = false;
      // Clear any pending delta derivation since no actual drag occurred
      this.pendingDeltaDerivation = null;
      return;
    }

    const { orderList, onUpdateOrderPrice, onDragStateChange } = this.props;
    const draggedOrder = orderList[override.index];

    // IMPORTANT: Signal drag end FIRST so OTO prices get committed to price store
    // This allows onUpdateOrderPrice to pick up correct OTO prices in single router call
    if (onDragStateChange) onDragStateChange(null);

    if (
      draggedOrder &&
      (draggedOrder.state === "local" || draggedOrder.state === "open")
    ) {
      console.log(
        "✅ OrderInteractive.handleDragComplete: Calling onUpdateOrderPrice:",
        {
          orderId: draggedOrder.id,
          newPrice: override.price,
          orderState: draggedOrder.state,
        },
      );
      // Commit main order new absolute price. OTO prices are already in price store from onDragStateChange(null).
      // This sends a SINGLE router call with main order + all OTO legs.
      onUpdateOrderPrice(draggedOrder.id, override.price);
      // NOTE: Removed redundant onOTOEdit loop - OTO updates are now handled via:
      // 1. commitPendingOTOUpdates (updates price store, no router call)
      // 2. onUpdateOrderPrice (sends single router call with main + OTO prices)
    } else {
      console.warn(
        "⚠️ OrderInteractive.handleDragComplete: Order not found or invalid state:",
        {
          index: override.index,
          draggedOrder: draggedOrder
            ? { id: draggedOrder.id, state: draggedOrder.state }
            : null,
        },
      );
    }
    this.currentOverride = null; // clear synchronous tracking
    this.setState({ override: null });
    this.mainOrderDragging = false;
    // Clear any pending delta derivation
    this.pendingDeltaDerivation = null;
  }

  handleSubmit(index) {
    const { orderList, onSubmitOrder, onDragStateChange } = this.props;
    const order = orderList[index];
    console.log("🔍 OrderInteractive.handleSubmit called:", {
      index,
      order,
      onSubmitOrder: !!onSubmitOrder,
    });
    if (order && order.state === "local") {
      if (onSubmitOrder) {
        onSubmitOrder(order.id);
        // Clear drag state after submission to restore crosshair
        if (onDragStateChange) onDragStateChange(null);
      }
    }
  }

  handleDelete(index) {
    const { orderList, onDeleteOrder, onDragStateChange } = this.props;
    const order = orderList[index];
    if (order && onDeleteOrder) {
      onDeleteOrder(order.id);
      // Clear drag state after deletion to restore crosshair
      if (onDragStateChange) onDragStateChange(null);
    }
  }

  handleStart() {}

  handleEnd(xyValue) {
    const { enabled, onCreateOrder, symbol } = this.props;
    if (!enabled || !symbol) return;
    const price = xyValue[1];
    const side = "buy";
    const quantity = 100; // TODO pull from defaults hook
    onCreateOrder(symbol, side, price, quantity);
  }

  handleDrawOrder() {}

  calculateButtonAreaWidth() {
    const trashButtonWidth = 30;
    const sendButtonWidth = 30;
    const gapBetweenButtons = 30;
    const backgroundPadding = 4;
    const spacing = 8;
    return (
      trashButtonWidth +
      gapBetweenButtons +
      sendButtonWidth +
      backgroundPadding +
      spacing
    );
  }

  render() {
    const { orderList } = this.props;
    const { override } = this.state;
    const orderOffsets = {};
    return (
      <g>
        {orderList.map((order, idx) => {
          const key = order.id || `order_${idx}`;
          const overridePrice = getValueFromOverride(
            override,
            idx,
            "price",
            order.price,
          );
          const offset = orderOffsets[idx] || 0;
          return (
            <EachOrderInteractive
              key={key}
              ref={this.saveNodeType(idx)}
              index={idx}
              order={{ ...order, price: overridePrice }}
              selected={order.selected || false}
              draggable={order.state === "local" || order.state === "open"}
              offset={offset}
              allOrders={orderList}
              onDragStart={() => this.handleDragStart(idx)}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
              onDragStateChange={this.props.onDragStateChange}
              onSubmit={this.handleSubmit}
              onDelete={this.handleDelete}
              onQuantityEdit={this.props.onQuantityEdit}
              onStopsClick={() =>
                this.props.onStopsClick && this.props.onStopsClick(order.id)
              }
              onOTORemove={this.props.onOTORemove}
              onOTOEdit={this.props.onOTOEdit}
              tempOTOPrices={this.props.tempOTOPrices}
            />
          );
        })}
      </g>
    );
  }
}

OrderInteractive.propTypes = {
  onCreateOrder: PropTypes.func.isRequired,
  onUpdateOrderPrice: PropTypes.func.isRequired,
  onUpdateOrderQuantity: PropTypes.func,
  onSubmitOrder: PropTypes.func.isRequired,
  onDeleteOrder: PropTypes.func,
  onDragStateChange: PropTypes.func,
  onQuantityEdit: PropTypes.func,
  onStopsClick: PropTypes.func,
  onOTORemove: PropTypes.func,
  onOTOEdit: PropTypes.func,
  tempOTOPrices: PropTypes.object,
  onSelect: PropTypes.func,
  orderList: PropTypes.array.isRequired,
  symbol: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired,
  appearance: PropTypes.object,
};

OrderInteractive.defaultProps = {
  onCreateOrder: noop,
  onUpdateOrderPrice: noop,
  onUpdateOrderQuantity: noop,
  onSubmitOrder: noop,
  onDeleteOrder: noop,
  onDragStateChange: noop,
  onSelect: noop,
  onQuantityEdit: noop,
  onStopsClick: noop,
  onOTORemove: noop,
  onOTOEdit: noop,
  tempOTOPrices: null,
  orderList: [],
  enabled: false,
  symbol: "",
  appearance: {},
};

OrderInteractive.contextTypes = {
  subscribe: PropTypes.func.isRequired,
  unsubscribe: PropTypes.func.isRequired,
  generateSubscriptionId: PropTypes.func.isRequired,
  chartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default OrderInteractive;
